/**
 * Breadcrumb Component
 *
 * Navigation breadcrumbs following the Portfolistic design system.
 * Supports dynamic paths, icons, and keyboard navigation.
 *
 * Features:
 * - Auto-generated from path or manual items
 * - Collapsible for long paths
 * - Icon support
 * - Keyboard accessible
 * - Mobile responsive
 *
 * Usage:
 * <Breadcrumb
 *   items={[
 *     { label: 'Home', href: '/' },
 *     { label: 'Projects', href: '/projects' },
 *     { label: 'My Project' },
 *   ]}
 * />
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronRight, Home, MoreHorizontal } from 'lucide-react';

/**
 * Breadcrumb Component
 */
const Breadcrumb = ({
  items = [],
  separator = <ChevronRight className="w-4 h-4" />,
  maxItems = 4,
  homeIcon = true,
  size = 'md',
  className = '',
  onNavigate,
}) => {
  const [expanded, setExpanded] = useState(false);

  // Size variants
  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // Determine which items to show
  const shouldCollapse = items.length > maxItems && !expanded;
  const visibleItems = shouldCollapse
    ? [items[0], { type: 'ellipsis' }, ...items.slice(-2)]
    : items;

  // Handle navigation
  const handleClick = (item, index, event) => {
    if (onNavigate) {
      event.preventDefault();
      onNavigate(item, index);
    }
  };

  // Handle keyboard
  const handleKeyDown = (item, index, event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (item.type === 'ellipsis') {
        setExpanded(true);
      } else if (onNavigate) {
        onNavigate(item, index);
      }
    }
  };

  return (
    <nav
      aria-label="Breadcrumb"
      className={`flex items-center ${sizeClasses[size]} ${className}`}
    >
      <ol className="flex items-center flex-wrap gap-1">
        {visibleItems.map((item, index) => {
          const isLast = index === visibleItems.length - 1;
          const isEllipsis = item.type === 'ellipsis';
          const isFirst = index === 0;

          return (
            <li
              key={isEllipsis ? 'ellipsis' : item.label || index}
              className="flex items-center"
            >
              {/* Separator (except for first item) */}
              {index > 0 && (
                <span
                  className="mx-2 text-gray-600"
                  aria-hidden="true"
                >
                  {separator}
                </span>
              )}

              {/* Ellipsis button */}
              {isEllipsis ? (
                <button
                  onClick={() => setExpanded(true)}
                  onKeyDown={(e) => handleKeyDown(item, index, e)}
                  className="flex items-center px-2 py-1 rounded-md
                           text-gray-500 hover:text-white hover:bg-white/10
                           transition-colors focus:outline-none focus:ring-2
                           focus:ring-primary-500/50"
                  aria-label={`Show ${items.length - 3} hidden items`}
                >
                  <MoreHorizontal className={iconSizes[size]} />
                </button>
              ) : isLast ? (
                /* Current page (not a link) */
                <span
                  className="flex items-center gap-1.5 px-2 py-1 text-white font-medium"
                  aria-current="page"
                >
                  {item.icon && (
                    <item.icon className={iconSizes[size]} aria-hidden="true" />
                  )}
                  {item.label}
                </span>
              ) : (
                /* Link to previous page */
                <a
                  href={item.href || '#'}
                  onClick={(e) => handleClick(item, index, e)}
                  onKeyDown={(e) => handleKeyDown(item, index, e)}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md
                           text-gray-400 hover:text-white hover:bg-white/10
                           transition-colors focus:outline-none focus:ring-2
                           focus:ring-primary-500/50"
                >
                  {isFirst && homeIcon ? (
                    <Home className={iconSizes[size]} aria-hidden="true" />
                  ) : item.icon ? (
                    <item.icon className={iconSizes[size]} aria-hidden="true" />
                  ) : null}
                  <span className={isFirst && homeIcon ? 'sr-only sm:not-sr-only' : ''}>
                    {item.label}
                  </span>
                </a>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

Breadcrumb.propTypes = {
  /** Array of breadcrumb items */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string,
      icon: PropTypes.elementType,
    })
  ).isRequired,
  /** Custom separator element */
  separator: PropTypes.node,
  /** Maximum items before collapsing */
  maxItems: PropTypes.number,
  /** Show home icon on first item */
  homeIcon: PropTypes.bool,
  /** Size variant */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Navigation callback */
  onNavigate: PropTypes.func,
};

/**
 * BreadcrumbFromPath - Auto-generate breadcrumbs from URL path
 *
 * Usage:
 * <BreadcrumbFromPath
 *   path="/projects/my-project/settings"
 *   labels={{ projects: 'Projects', settings: 'Settings' }}
 * />
 */
export const BreadcrumbFromPath = ({
  path = '',
  labels = {},
  homeLabel = 'Home',
  ...props
}) => {
  const segments = path.split('/').filter(Boolean);

  const items = [
    { label: homeLabel, href: '/' },
    ...segments.map((segment, index) => ({
      label: labels[segment] || formatSegment(segment),
      href: '/' + segments.slice(0, index + 1).join('/'),
    })),
  ];

  return <Breadcrumb items={items} {...props} />;
};

/**
 * Format URL segment to readable label
 */
const formatSegment = (segment) => {
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

BreadcrumbFromPath.propTypes = {
  path: PropTypes.string.isRequired,
  labels: PropTypes.object,
  homeLabel: PropTypes.string,
};

/**
 * useBreadcrumbs - Hook for managing breadcrumb state
 *
 * Usage:
 * const { items, push, pop, set } = useBreadcrumbs([{ label: 'Home', href: '/' }]);
 */
export const useBreadcrumbs = (initialItems = []) => {
  const [items, setItems] = React.useState(initialItems);

  const push = React.useCallback((item) => {
    setItems((prev) => [...prev, item]);
  }, []);

  const pop = React.useCallback(() => {
    setItems((prev) => prev.slice(0, -1));
  }, []);

  const set = React.useCallback((newItems) => {
    setItems(newItems);
  }, []);

  const reset = React.useCallback(() => {
    setItems(initialItems);
  }, [initialItems]);

  return { items, push, pop, set, reset };
};

export default Breadcrumb;
